import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  chatsApi, leadsApi, watiApi, messagesApi, quickRepliesApi, filtersApi, remindersApi,
  aiApi, leadActionsApi, paymentApi, labelsApi, scheduledMessagesApi, agentActivityApi,
  escalationApi, orderTrackingApi, booksApi,
  zohoApi, translateApi, telecmiApi, settingsApi, teamNotifyApi,
} from "../lib/api";
import { detectPhoneCountry } from "../lib/phoneCountry";
import { getCourierTrackingLink } from "../lib/courierTracking";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Search, RefreshCw, MessageCircle, Phone, Loader2, Send, User,
  ShoppingBag, Tag, Clock, ArrowLeft, Zap, Plus, X, Pencil, Trash2, Image as ImageIcon,
  Filter, CalendarPlus, Check, ChevronDown, ChevronUp, Sparkles, Brain, Clipboard, Package, MapPin, Mail,
  AlertTriangle, UserPlus, IndianRupee, Copy, ExternalLink, QrCode, Download,
  MoreVertical, CheckCircle, XCircle, Eye, EyeOff, MailOpen, MailCheck, Paperclip, CreditCard, Percent,
  CornerUpLeft, Truck, GitBranch, MessageSquarePlus, Languages, PhoneCall, UserSearch,
  DollarSign, Euro, PoundSterling, JapaneseYen,
} from "lucide-react";
import NewChatModal from "../components/NewChatModal";
import Customer360Panel from "../components/Customer360Panel";
import ImagePreviewModal from "../components/chat/ImagePreviewModal";
import ChatHeaderCallButton from "../components/chat/ChatHeaderCallButton";
import WebsiteOrdersPanel from "../components/chat/WebsiteOrdersPanel";
import AdHocCouponCard from "../components/chat/AdHocCouponCard";
import TemplateManager from "../components/chat/TemplateManager";
import ZohoEmailTab from "../components/chat/ZohoEmailTab";
import { ProductLookupTab } from "../components/chat/ProductLookupTab";
import {
  looksNonEnglish,
  getResponseTimer, formatTime,
} from "../components/chat/utils";
import ChatListSidebar from "../components/chat/ChatListSidebar";
import ChatHeader from "../components/chat/ChatHeader";
import ExportChatModal from "../components/chat/ExportChatModal";
import ShareChatModal from "../components/chat/ShareChatModal";
import ReplyComposer from "../components/chat/ReplyComposer";
import MessageThread from "../components/chat/MessageThread";
import NotesSection from "../components/chat/NotesSection";
import AgentToolsPanel from "../components/chat/AgentToolsPanel";
import OrderTab from "../components/chat/OrderTab";
// Iter 156.2 (Feb 27, 2026, Yash) — Modal extractions to bring this file
// down from 2,544 → ~2,000 lines. Each modal is fully self-contained;
// props in, callbacks out.
import { CurrencyIcon } from "../components/chat/CurrencyIcon";
import { PaymentLinkModal } from "../components/chat/PaymentLinkModal";
import { CreateZohoTicketModal } from "../components/chat/CreateZohoTicketModal";
import { NotifyTeamModal } from "../components/chat/NotifyTeamModal";
import { ImportContactModal } from "../components/chat/ImportContactModal";
import { PaymentVerificationModal } from "../components/chat/PaymentVerificationModal";
import { ImageSearchResultsPanel } from "../components/chat/ImageSearchResultsPanel";
// Iter 156.3 (Feb 27, 2026, Yash) — Action-cluster hooks. Each hook receives
// the state setters it needs as props and returns a fully-formed handler
// suite, so the parent JSX stays declarative. Keeps state co-located here.
import { usePaymentLinkActions } from "../hooks/usePaymentLinkActions";
import { useAiActions } from "../hooks/useAiActions";
import { useLabelActions } from "../hooks/useLabelActions";
import { useDepartmentActions } from "../hooks/useDepartmentActions";
// Iter 156.22 (Yash, Feb 28 2026) — Phase A of ChatsPage split. Three
// self-contained state clusters lifted into dedicated hooks; the visible
// behaviour and the props that `<NotesSection>`, `<InfoTab>`, and
// `<ChatListSidebar>` already expected are unchanged.
import useChatNotes from "../hooks/useChatNotes";
import useChatIssueTags from "../hooks/useChatIssueTags";
import useChatBulkActions from "../hooks/useChatBulkActions";

import {
  InfoTab,
  Sec, Row, CategoryBadge,
  getLabelColor as _getLabelColor,
  PRESET_LABELS as _PRESET_LABELS,
} from "../components/chat/ChatRightPanel";

const CATEGORIES = ["Books", "Sculptures", "Paintings", "Clothing", "Jewelry", "Home and Living"];

const CATEGORY_COLORS = {
  "Books": { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  "Paintings": { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  "Sculptures": { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  "Clothing": { bg: "bg-teal-50", text: "text-teal-700", dot: "bg-teal-500" },
  "Jewelry": { bg: "bg-pink-50", text: "text-pink-700", dot: "bg-pink-500" },
  "Home and Living": { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
};
const DEFAULT_CAT_COLOR = { bg: "bg-gray-50", text: "text-gray-600", dot: "bg-gray-400" };

// Label colors + preset list — re-exported from ChatRightPanel so we
// keep a single source of truth but still reachable from ChatsPage under
// the old names.
const getLabelColor = _getLabelColor;
const PRESET_LABELS = _PRESET_LABELS;

/**
 * Currency icon picker for the payment-link modal. Mirrors the currency
 * dropdown so the icon next to the amount input flips between ₹ / $ / € /
 * £ / ¥ / etc instead of always showing the rupee mark. Returns
 * `IndianRupee` as the safe fallback for any code we don't have a
 * dedicated icon for (e.g. AED / SGD — Stripe accepts them but lucide
 * doesn't ship a glyph).
 */
// CurrencyIcon was inline here; moved to components/chat/CurrencyIcon.jsx (iter 156.2).


// Iter 132 (Apr 30, 2026) — Status filter tabs.
// Iter 153 (Feb 6, 2026, Yash) — CRM now owns the status enum entirely
// (OPEN / SOLVED / EXPIRED). PENDING dropped (legacy data with PENDING
// is treated as OPEN by the backend filter). Solved tab kept here so
// agents can browse what they closed; the Solved BUTTON was removed
// from the chat header (different control).
const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "open", label: "Open" },
  { key: "solved", label: "Solved" },
  { key: "expired", label: "Expired" },
  { key: "about_to_expire", label: "About to Expire" },
];

const catColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_CAT_COLOR;

// Returns true if wati_status belongs in the given statusFilter tab.
// Used to evict a chat from the list immediately after its status changes.
const _statusMatchesFilter = (watiStatus, statusFilter) => {
  const s = (watiStatus || "").toUpperCase();
  const f = (statusFilter || "").toLowerCase();
  if (!f) return true; // "All" tab — always matches
  if (f === "open" || f === "active") return ["OPEN", "PENDING", "", null, undefined].includes(s || null);
  if (f === "solved" || f === "closed") return s === "SOLVED";
  if (f === "expired") return s === "EXPIRED";
  return true;
};

const ChatsPage = () => {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [show360Phone, setShow360Phone] = useState(null);
  const [previewImage, setPreviewImage] = useState(null); // legacy single url (kept for any callers)
  const [previewGallery, setPreviewGallery] = useState(null); // { images: string[], index: number }
  // Filters
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterAgent, setFilterAgent] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [filterDepartment, setFilterDepartment] = useState([]);
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | unread | longest_waiting | alphabetical
  const [agents, setAgents] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  // Follow-up
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpMessage, setFollowUpMessage] = useState("");
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  // AI
  const [aiLoading, setAiLoading] = useState("");
  // Details panel tab
  const [detailsTab, setDetailsTab] = useState("info");
  // Customer form
  const [custForm, setCustForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", customer_address: "", customer_pincode: "", amount: "", discount_percent: "", discount_amount: "", payment_mode: "COD", notes: "", item_codes: "" });
  const [savingCust, setSavingCust] = useState(false);
  // Record order
  const [orderNum, setOrderNum] = useState("");
  const [orderAmt, setOrderAmt] = useState("");
  // Payment link
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDesc, setPaymentDesc] = useState("");
  const [paymentCurrency, setPaymentCurrency] = useState("INR");
  // Provider toggle — Razorpay (India) or Stripe (overseas cards). PayPal
  // arrives once the user pastes their Sandbox creds. We default to Razorpay
  // for INR and auto-switch to Stripe the moment the agent picks any other
  // currency.
  const [paymentProvider, setPaymentProvider] = useState("razorpay");
  // Provider feature-flag config from backend (enabled / disabled per rail).
  // Stripe stays disabled while only a test key is configured — Yash, Apr 28
  // 2026 — so test links never reach a real customer.
  const [providersConfig, setProvidersConfig] = useState(null);
  const [payCustomerName, setPayCustomerName] = useState("");
  const [payCustomerPhone, setPayCustomerPhone] = useState("");
  const [payCustomerEmail, setPayCustomerEmail] = useState("");
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [qrBase64, setQrBase64] = useState(null);
  const [sendingQR, setSendingQR] = useState(false);
  const [showChatActions, setShowChatActions] = useState(false);
  const [sendingPaymentQR, setSendingPaymentQR] = useState(false);
  const [watiOperatorEmail, setWatiOperatorEmail] = useState("");
  const [imageSearchResults, setImageSearchResults] = useState(null);
  const [searchingImage, setSearchingImage] = useState(false);
  // Import contact modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [importPhone, setImportPhone] = useState("");
  const [importing, setImporting] = useState(false);
  // Attachment
  const [attachFile, setAttachFile] = useState(null);
  const [attachCaption, setAttachCaption] = useState("");
  const [sendingAttach, setSendingAttach] = useState(false);
  const attachInputRef = useRef(null);
  // Reply to specific message
  const [replyToMsg, setReplyToMsg] = useState(null);
  // Drag & drop
  const [isDragging, setIsDragging] = useState(false);
  // Attachment preview URL
  const [attachPreviewUrl, setAttachPreviewUrl] = useState(null);
  // Labels
  const [allLabels, setAllLabels] = useState([]);
  const [filterLabel, setFilterLabel] = useState([]);
  const [newLabelText, setNewLabelText] = useState("");
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  // Memoise the merged preset+custom label set — re-computing on every
  // keystroke is wasteful given the filter pill renders on every key change.
  const labelOptions = useMemo(
    () => [...new Set([...PRESET_LABELS, ...allLabels])],
    [allLabels]
  );

  // Client-side sort wrapper. The server already returns chats in the right
  // order, BUT local mutations (active-chat polls, status toggles, etc.) can
  // shuffle `last_message_at` without re-querying — so the visible order
  // would slowly drift away from the chosen sort. This memo re-stabilises
  // the order using the same rules as the server's `_SORT_SPECS`.
  const sortedChats = useMemo(() => {
    if (!Array.isArray(chats) || chats.length < 2) return chats;
    // Numeric timestamp parser — empty/invalid values land at the bottom in
    // descending order and at the top in ascending. Matches MongoDB's null
    // handling so the user sees consistent positions across sort flips.
    const tsOf = (c) => {
      const v = c?.last_message_at || c?.updated_at || "";
      const n = v ? Date.parse(v) : NaN;
      return Number.isFinite(n) ? n : 0;
    };
    const arr = [...chats];
    if (sortBy === "oldest" || sortBy === "longest_waiting") {
      arr.sort((a, b) => tsOf(a) - tsOf(b));
    } else if (sortBy === "alphabetical") {
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    } else if (sortBy === "unread") {
      arr.sort((a, b) => {
        const u = (Number(b?.unread_count) || 0) - (Number(a?.unread_count) || 0);
        return u !== 0 ? u : tsOf(b) - tsOf(a);
      });
    } else {
      // newest (default) — descending by last_message_at
      arr.sort((a, b) => tsOf(b) - tsOf(a));
    }
    return arr;
  }, [chats, sortBy]);
  const [showMobileHeaderMenu, setShowMobileHeaderMenu] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleType, setScheduleType] = useState("whatsapp");
  // Agent activity & auto-reply status
  const [agentActivity, setAgentActivity] = useState(null);
  const [autoReplyActive, setAutoReplyActive] = useState(false);
  // Iter 156.22 — Phase A. Three clusters extracted into dedicated hooks
  // below (after `fetchChats` is defined, since `useChatBulkActions` needs
  // it as the refetch callback). State that USED to live here:
  //   • notes / newNote        → useChatNotes
  //   • issueTags (dead state) / availableIssueTags → useChatIssueTags
  //   • bulkMode / selectedIds / showBulkMenu       → useChatBulkActions
  // Escalation
  // Iter 156.2 — `showEscalateModal` removed (orphan: never wired to a trigger).
  // The actual escalation flow now routes through the Create Email Ticket modal
  // (showCreateTicket) via the `escalate-to-email-btn` in the chat header.
  // `handleEscalate` is preserved for the legacy API call signature in case
  // we wire a direct-escalation button back later.
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [ticketInternal, setTicketInternal] = useState(true);   // default to Internal per user request
  const [ticketEmailOverride, setTicketEmailOverride] = useState("help@exoticindia.com");
  // Notify-team modal (manual chat-to-Sales/CS email button)
  const [showNotifyTeamModal, setShowNotifyTeamModal] = useState(false);
  // Iter 153.43 (Feb 9, 2026, Yash) — Export / Share modals
  const [showExportModal, setShowExportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [notifyTeamConfig, setNotifyTeamConfig] = useState({ recipients: [], reasons: [] });
  const [notifyRecipient, setNotifyRecipient] = useState("");
  const [notifyReason, setNotifyReason] = useState("");
  const [notifyNote, setNotifyNote] = useState("");
  const [notifySending, setNotifySending] = useState(false);
  // Order tracking
  const [orderLookup, setOrderLookup] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [detectedOrders, setDetectedOrders] = useState([]);
  // Product lookup (Books API)
  const [productLookup, setProductLookup] = useState("");
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productSuggestions, setProductSuggestions] = useState(null); // null = no error, [] = empty suggestions, [...] = chips to show
  const [detectedProducts, setDetectedProducts] = useState([]);
  const [priceScope, setPriceScope] = useState("INR"); // INR or USD — driven by lead country

  const pollRef = useRef(null);
  const msgEndRef = useRef(null);
  const chatPollRef = useRef(null);
  const inputRef = useRef(null);
  const chatListRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const msgPollRef = useRef(null);
  const dropZoneRef = useRef(null);
  const PAGE_SIZE = 30;

  // Helper: set attachment file and create/revoke preview URL
  const setAttachment = useCallback((file) => {
    if (attachPreviewUrl) URL.revokeObjectURL(attachPreviewUrl);
    if (file) {
      setAttachFile(file);
      const isMedia = file.type.startsWith("image/") || file.type.startsWith("video/");
      setAttachPreviewUrl(isMedia ? URL.createObjectURL(file) : null);
    } else {
      setAttachFile(null);
      setAttachPreviewUrl(null);
      setAttachCaption("");
    }
  }, [attachPreviewUrl]);

  // Detect preview vs live mode for polling intervals
  const isPreview = window.location.hostname.includes("preview");
  const CHAT_LIST_POLL_MS = isPreview ? 120000 : 8000;    // 2min preview, 8s live
  const ACTIVE_CHAT_POLL_MS = isPreview ? 60000 : 3000;   // 1min preview, 3s live

  // Lock body scroll when ChatsPage is mounted to prevent viewport shifting
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Fetch payment-provider config once on mount so the modal can grey-out
  // disabled rails (e.g. Stripe while only a test key is configured —
  // Yash, Apr 28 2026, "make sure no real-customer links go through test
  // keys"). Failure is non-fatal; we just default-show all tiles.
  useEffect(() => {
    paymentApi.getProvidersConfig()
      .then(r => setProvidersConfig(r.data))
      .catch(() => setProvidersConfig(null));
  }, []);

  // Mobile UX — when a chat is open, hide the bottom nav (mirrors
  // WhatsApp/Telegram). The body.chat-open class is consumed by CSS
  // in index.css. Cleans up on unmount + when chat is deselected so the
  // user always sees the nav on the chat-list view.
  useEffect(() => {
    if (selectedChat) document.body.classList.add("chat-open");
    else document.body.classList.remove("chat-open");
    return () => document.body.classList.remove("chat-open");
  }, [selectedChat]);

  const fetchChats = useCallback(async (pageNum = 1, append = false, isBackground = false) => {
    if (pageNum === 1 && !append && !isBackground) setLoading(true);
    else if (append) setLoadingMore(true);
    // Background polls: no loading state, silent update
    try {
      const params = { limit: PAGE_SIZE, page: pageNum };
      if (filterCategory.length) params.category = filterCategory.join(",");
      if (filterAgent.length) params.assigned_to = filterAgent.join(",");
      if (search) params.search = search;
      if (statusFilter) params.status_filter = statusFilter;
      if (filterLabel.length) params.label = filterLabel.join(",");
      if (countryFilter) params.country = countryFilter;
      if (filterDepartment.length) params.department = filterDepartment.join(",");
      if (sortBy && sortBy !== "newest") params.sort = sortBy;
      const response = await chatsApi.getAll(params);
      const newChats = response.data.chats;
      if (append) {
        setChats(prev => {
          const ids = new Set(prev.map(c => c.id));
          return [...prev, ...newChats.filter(c => !ids.has(c.id))];
        });
      } else if (isBackground) {
        // Use server sort order (sorted by last_message_at desc) for page 1,
        // then append any previously-loaded scroll items not in current page
        setChats(prev => {
          const newSet = new Set(newChats.map(c => c.id));
          const scrolledChats = prev.filter(c => !newSet.has(c.id));
          return [...newChats, ...scrolledChats];
        });
      } else {
        setChats(newChats);
      }
      setTotal(response.data.total);
      setHasMore(newChats.length === PAGE_SIZE);
      if (!isBackground) setPage(pageNum);
    } catch (error) {
      if (!isBackground) toast.error("Failed to load chats");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filterCategory, filterAgent, search, statusFilter, filterLabel, countryFilter, filterDepartment, sortBy]);

  const fetchQuickReplies = useCallback(async () => {
    try { const res = await quickRepliesApi.getAll(); setQuickReplies(res.data.templates || []); } catch (e) {}
  }, []);
  const fetchFilterOptions = useCallback(async () => {
    try { const res = await filtersApi.getOptions(); setAgents(res.data.agents || []); } catch (e) {}
  }, []);
  const fetchLabels = useCallback(async () => {
    try { const res = await labelsApi.getAll(); setAllLabels(res.data.labels || []); } catch (e) {}
  }, []);
  const fetchAgentActivity = async () => {
    try {
      const res = await agentActivityApi.get();
      setAgentActivity(res.data.agents || []);
      setAutoReplyActive(res.data.auto_reply_active || false);
    } catch (e) {}
  };

  // Iter 156.22 (Phase A) — three state clusters moved into hooks.
  // Order matters: useChatBulkActions needs `fetchChats` (defined above).
  const {
    notes, newNote, setNewNote, fetchNotes, addNote, deleteNote,
  } = useChatNotes(selectedLead);
  const { availableIssueTags, fetchIssueTags, toggleIssueTag } =
    useChatIssueTags(selectedLead, setSelectedLead);
  const {
    bulkMode, setBulkMode,
    selectedIds, setSelectedIds,
    setShowBulkMenu,
    toggleBulkSelect, selectAllVisible, executeBulkAction,
  } = useChatBulkActions(chats, fetchChats);

  // Escalation
  const handleCreateZohoTicket = async () => {
    if (!selectedLead) return;
    if (!ticketSubject.trim() || !ticketDescription.trim()) {
      toast.error("Subject and description required");
      return;
    }
    // Pick the recipient: internal mode → the help-desk (or overridden) address;
    // customer mode → the lead's email on file.
    const recipientEmail = ticketInternal
      ? (ticketEmailOverride.trim() || "help@exoticindia.com")
      : (selectedLead.customer_email || "").trim();
    if (!recipientEmail) {
      toast.error("No recipient email — either toggle Internal or add a customer email.");
      return;
    }
    setCreatingTicket(true);
    try {
      // When internal: build a richer description so the help-desk sees who the
      // customer is (phone / name / lead link) without exposing the thread to them.
      let description = ticketDescription.trim();
      if (ticketInternal) {
        const ctxLines = [
          "--- Customer context ---",
          `Name: ${selectedLead.customer_name || selectedLead.name || "—"}`,
          `Phone: ${selectedLead.customer_phone || selectedLead.phone || "—"}`,
          selectedLead.customer_email ? `Email: ${selectedLead.customer_email}` : null,
          selectedLead.department ? `Department: ${selectedLead.department}` : null,
          selectedLead.last_order_number ? `Last order: ${selectedLead.last_order_number}` : null,
          `CRM lead: ${selectedLead.id}`,
        ].filter(Boolean);
        description = `${description}\n\n${ctxLines.join("\n")}`;
      }
      const res = await zohoApi.createTicket({
        contact_name: ticketInternal
          ? "Exotic India — Internal"
          : (selectedLead.customer_name || selectedLead.name),
        contact_email: recipientEmail,
        contact_phone: ticketInternal ? "" : (selectedLead.customer_phone || selectedLead.phone),
        subject: ticketSubject.trim(),
        description,
        lead_id: selectedLead.id,
        priority: "Medium",
        is_internal: ticketInternal,
      });
      toast.success(`Ticket #${res.data.ticket?.ticketNumber} created${ticketInternal ? " (Internal)" : ""}`);
      setShowCreateTicket(false);
      setTicketSubject("");
      setTicketDescription("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleEscalate = async () => {
    if (!selectedLead) return;
    // Iter 156.2 — `escalateReason` state removed (orphan modal cleanup). The
    // backend still accepts an optional reason; passing empty string keeps
    // the call signature stable so anyone re-wiring this gets the original
    // behaviour.
    try {
      await escalationApi.escalate(selectedLead.id, "");
      toast.success("Chat escalated to admin");
      fetchChats(1);
    } catch { toast.error("Escalation failed"); }
  };

  // Load Notify-team config when the modal opens (cached after first load)
  useEffect(() => {
    if (!showNotifyTeamModal) return;
    if ((notifyTeamConfig.recipients || []).length > 0) return;
    settingsApi.getTeamNotify()
      .then((res) => setNotifyTeamConfig(res.data || { recipients: [], reasons: [] }))
      .catch(() => toast.error("Failed to load team-notify config"));
  }, [showNotifyTeamModal, notifyTeamConfig.recipients]);

  const handleSendNotifyTeam = async () => {
    if (!selectedLead || !notifyRecipient || !notifyReason) return;
    setNotifySending(true);
    try {
      const res = await teamNotifyApi.send(selectedLead.id, {
        recipient_email: notifyRecipient,
        reason_value: notifyReason,
        note: notifyNote.trim(),
      });
      if (res.data?.ok) {
        toast.success(`Email sent to ${notifyRecipient}`);
        setShowNotifyTeamModal(false);
        setNotifyReason("");
        setNotifyNote("");
        // Keep recipient selected for fast repeat sends
      } else {
        toast.error(res.data?.error || "Email failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setNotifySending(false);
    }
  };

  // Order tracking
  const lookupOrder = async (num) => {
    const orderNum = num || orderLookup.trim();
    if (!orderNum) return;
    setOrderLoading(true);
    try {
      const res = await orderTrackingApi.lookup(orderNum);
      if (res.data.error) { toast.error(res.data.error); setOrderData(null); }
      else setOrderData(res.data);
    } catch { toast.error("Order lookup failed"); }
    finally { setOrderLoading(false); }
  };
  const detectOrders = async (leadId) => {
    try {
      const res = await orderTrackingApi.detectInChat(leadId);
      setDetectedOrders(res.data.orders || []);
      if (res.data.order_numbers?.length > 0 && res.data.orders?.length === 0) {
        // Numbers found but API didn't return data
        setDetectedOrders([]);
      }
    } catch { setDetectedOrders([]); }
  };

  const detectProducts = async (leadId) => {
    try {
      const res = await booksApi.detectInChat(leadId);
      setDetectedProducts(res.data.products || []);
      setPriceScope(res.data.price_scope || "INR");
    } catch { setDetectedProducts([]); }
  };

  // Product / Book lookup by item code
  const lookupProduct = async (code) => {
    const itemcode = (code || productLookup).trim();
    if (!itemcode) return;
    setProductLoading(true);
    setProductSuggestions(null);
    try {
      const res = await booksApi.lookup(itemcode);
      setProductData(res.data);
    } catch (e) {
      setProductData(null);
      // Backend now returns a structured 404 with suggestions for "Did you mean…?".
      // Older shape `detail` was a plain string — handle both.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.suggestions)) {
        setProductSuggestions(detail.suggestions);
        toast.error(detail.message || "Product not found");
      } else {
        setProductSuggestions([]);
        toast.error((typeof detail === "string" ? detail : null) || "Product not found");
      }
    } finally {
      setProductLoading(false);
    }
  };

  const sendProductSummary = async () => {
    if (!productData || !selectedLead) return;
    const msg = buildProductDetailsMessage(productData);
    if (!msg) return;
    try {
      await messagesApi.create({
        lead_id: selectedLead.id,
        content: msg,
        direction: "outgoing",
        send_whatsapp: true,
      });
      toast.success("Product details sent");
      await loadMessages(selectedLead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const copyProductSummary = () => {
    if (!productData) return;
    const msg = buildProductDetailsMessage(productData);
    if (!msg) return;
    navigator.clipboard?.writeText(msg);
    toast.success("Copied to clipboard");
  };

  // Build a price-free, WhatsApp-ready product details message. We intentionally strip
  // price/discount per business rule — price needs to be quoted per-customer based on
  // their country and (soon) discount rules. Agents get the raw numbers in the UI.
  const buildProductDetailsMessage = (pd) => {
    if (!pd || !pd.raw) return "";
    const det = pd.raw.details || {};
    const log = pd.raw.logistics || {};
    const category = pd.category || pd.raw.category || "";
    const lines = [`*${det.title || det.name || pd.itemcode}*`, `Item Code: ${pd.itemcode}`];
    if (category) lines.push(`Category: ${category.replace(/^./, c => c.toUpperCase())}`);
    if (det.author) lines.push(`Author: ${det.author}`);
    if (det.publisher) lines.push(`Publisher: ${det.publisher}`);
    if (det.pages) lines.push(`Pages: ${det.pages}`);
    if (det.language) lines.push(`Language: ${det.language}`);
    if (det.edition) lines.push(`Edition: ${det.edition}`);
    if (det.binding) lines.push(`Binding: ${det.binding}`);
    if (det.isbn) lines.push(`ISBN: ${det.isbn}`);
    if (det.color) lines.push(`Color: ${det.color}`);
    if (det.size) lines.push(`Size: ${det.size}`);
    const wDisplay = pd.logistics_resolved?.weight_display;
    if (wDisplay) lines.push(`Weight: ${wDisplay}`);
    if (Array.isArray(pd.variants) && pd.variants.length > 1) {
      lines.push(`Variants: ${pd.variants.length} color/size options available`);
    }
    if (log.leadtime) lines.push(`Ships within ${parseInt(log.leadtime)} days of order`);
    return lines.join("\n");
  };

  // Resend a FAILED outgoing message through WATI
  const handleResendMessage = async (msg) => {
    if (!msg?.id) return;
    try {
      await messagesApi.resend(msg.id);
      toast.success("Message resent successfully");
      if (selectedLead) await loadMessages(selectedLead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  // AI-verify a customer-sent payment screenshot. Opens the verdict modal
  // with the structured result from Claude Vision (amount, ref, payer…).
  const [verifyingPaymentMsgId, setVerifyingPaymentMsgId] = useState(null);
  const [paymentVerdict, setPaymentVerdict] = useState(null);
  const handleVerifyPayment = async (msg) => {
    if (!msg?.id) return;
    setVerifyingPaymentMsgId(msg.id);
    setPaymentVerdict(null);
    try {
      const res = await messagesApi.verifyPayment(msg.id);
      setPaymentVerdict(res.data);
      if (res.data?.status === "paid") {
        toast.success("✅ Payment verified — lead tagged as Paid.");
      } else if (res.data?.status === "suspicious") {
        toast.warning("⚠️ Suspicious — see details.");
      } else {
        toast.error("❌ Not a valid payment screenshot.");
      }
      // Refresh messages so the system note appears in the chat.
      if (selectedLead) await loadMessages(selectedLead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
      setVerifyingPaymentMsgId(null);
    }
  };

  // Set department (Sales / Customer Support) on the selected lead.
  // The backend also auto-reassigns to that department's leader and pauses
  // AI on the chat so the new team takes over cleanly.
  // Iter 156.3 — useDepartmentActions invocation moved below loadMessages
  // declaration (line ~919) to avoid Temporal Dead Zone ReferenceError.
  // See test iteration_54.json RCA.


  useEffect(() => {
    fetchChats(1);
    fetchQuickReplies();
    fetchFilterOptions();
    fetchLabels();
    checkSyncStatus();
    fetchAgentActivity();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // Mount-only initialiser. The bulk fetches above are intentionally
    // run once; per-filter refetches happen in dedicated effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Background poll — recreate interval whenever fetchChats changes (i.e., when filters change)
  useEffect(() => {
    if (chatPollRef.current) clearInterval(chatPollRef.current);
    chatPollRef.current = setInterval(() => fetchChats(1, false, true), CHAT_LIST_POLL_MS);
    return () => { if (chatPollRef.current) clearInterval(chatPollRef.current); };
  }, [fetchChats, CHAT_LIST_POLL_MS]);

  // Re-fetch when filters change (skip initial mount since first paint
  // already triggered the fetchChats-driven effect above).
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
    setHasMore(true);
    fetchChats(1);
    // `fetchChats` is intentionally excluded — this effect triggers an
    // *immediate* fetch when filter inputs change. Adding fetchChats would
    // double-fire because fetchChats's own dep list already includes these
    // same filters, causing a redundant render → re-create → re-fire loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory, filterAgent, statusFilter, filterLabel, countryFilter, filterDepartment, sortBy]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchChats(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
    // Same reasoning as above — fetchChats is filter-derived; we only want
    // to re-debounce when the search input itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Auto-poll for new messages on the active chat (every 10s)
  useEffect(() => {
    if (msgPollRef.current) clearInterval(msgPollRef.current);
    if (!selectedChat) return;
    const pollActiveChat = async () => {
      try {
        const res = await leadsApi.refreshMessages(selectedChat.id);
        if (res.data.new_messages > 0) {
          setMessages(res.data.messages);
          // Update chat list to reflect new message
          setChats(prev => prev.map(c => c.id === selectedChat.id ? { ...c, last_message_at: new Date().toISOString(), unread_count: 0 } : c));
          setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        }
      } catch (e) { /* silent */ }
    };
    msgPollRef.current = setInterval(pollActiveChat, ACTIVE_CHAT_POLL_MS);
    return () => { if (msgPollRef.current) clearInterval(msgPollRef.current); };
  }, [selectedChat, ACTIVE_CHAT_POLL_MS]);

  // Infinite scroll handler
  const handleChatListScroll = useCallback(() => {
    const el = chatListRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
      fetchChats(page + 1, true);
    }
  }, [loadingMore, hasMore, page, fetchChats]);

  /**
   * Open the fullscreen preview with a gallery of every image in the current
   * chat, so the agent can swipe left/right (or tap arrows / press arrow keys)
   * to flip through all customer-sent images without closing each one.
   * `clickedUrl` is the specific image the agent tapped on — it becomes the
   * starting slide.
   */
  const openImageGallery = useCallback((clickedUrl) => {
    // Re-build the same URL logic MessageBubble uses so the gallery URLs
    // match (otherwise duplicate detection fails). HYBRID AUTH: append
    // `?token=` to /api media URLs so `<img>` tags work even when the
    // httpOnly cookie is blocked cross-origin.
    const _tk = (() => { try { return localStorage.getItem("crm_token") || ""; } catch { return ""; } })();
    const toFull = (u) => {
      if (typeof u !== "string" || !u) return null;
      if (!u.startsWith("/api/")) return u;
      const withTok = _tk ? (u + (u.includes("?") ? "&" : "?") + "token=" + encodeURIComponent(_tk)) : u;
      return `${process.env.REACT_APP_BACKEND_URL}${withTok}`;
    };
    const isImageUrl = (u, type) => {
      const s = String(type || "").toLowerCase();
      return s.includes("image") ||
        (u && (u.includes("/images/") || u.includes("image") ||
               /\.(jpe?g|png|webp|gif|bmp|heic)(\?|$)/i.test(u)));
    };
    const images = [];
    const seen = new Set();
    for (const m of messages) {
      if (!m.media_url) continue;
      const type = [m.wati_type, m.messageType, m.type, m.mime_type].filter(Boolean).join(" ");
      if (!isImageUrl(m.media_url, type)) continue;
      const full = toFull(m.media_url);
      if (full && !seen.has(full)) { seen.add(full); images.push(full); }
    }
    const index = Math.max(0, images.indexOf(clickedUrl));
    if (images.length === 0) {
      // Fallback: just show the clicked image alone
      setPreviewGallery({ images: [clickedUrl], index: 0 });
    } else {
      setPreviewGallery({ images, index });
    }
  }, [messages]);

  const checkSyncStatus = async () => {
    try {
      const res = await watiApi.getSyncStatus();
      if (res.data.status === "running") { setSyncing(true); setSyncProgress(res.data); startPolling(); }
    } catch (e) {}
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await watiApi.getSyncStatus();
        setSyncProgress(res.data);
        if (res.data.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null; setSyncing(false);
          if (res.data.status === "completed") {
            toast.success(`Sync complete`); fetchChats(1, false, true);
          }
        }
      } catch (e) {}
    }, 3000);
  };

  const handleSyncAll = async () => {
    setSyncing(true); setSyncProgress({ status: "starting", progress: 0, total: 0 });
    try { await watiApi.sync(); toast.info("Contact sync started"); startPolling(); }
    catch (error) { toast.error("Failed to start sync"); setSyncing(false); }
  };

  const handleSyncMessages = async () => {
    setSyncing(true); setSyncProgress({ status: "starting", progress: 0, total: 0 });
    try { await watiApi.syncMessages(); toast.info("Message sync started"); startPolling(); }
    catch (error) { toast.error("Failed to start sync"); setSyncing(false); }
  };

  const handleImportContact = async () => {
    if (!importPhone.trim()) {
      toast.error("Please enter a phone number");
      return;
    }
    setImporting(true);
    try {
      const response = await watiApi.importContact(importPhone.trim().replace(/[^0-9]/g, ''));
      const data = response.data;
      if (data.status === "exists") {
        toast.info(`Contact "${data.name}" already exists`);
      } else if (data.status === "imported") {
        toast.success(`Imported "${data.name}" with ${data.messages_imported} messages`);
        fetchChats(1, false, true); // Refresh chat list
      }
      setShowImportModal(false);
      setImportPhone("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to import contact";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const loadMessages = useCallback(async (chatId) => {
    setLoadingMessages(true);
    try {
      const response = await leadsApi.getOne(chatId);
      const data = response.data;
      const lead = { ...data.lead, orders: data.orders, reminders: data.reminders };
      setSelectedLead(lead);
      setMessages(data.messages || []);
      // Prefill customer form
      setCustForm({
        customer_name: lead.customer_name || lead.name || "",
        customer_email: lead.customer_email || "",
        customer_phone: lead.customer_phone || lead.phone || "",
        customer_address: lead.customer_address || "",
        customer_pincode: lead.customer_pincode || "",
        amount: "",
        discount_percent: "",
        discount_amount: "",
        payment_mode: "COD",
        notes: lead.notes || "",
        item_codes: "",
      });
      // Clear unread in local state
      setChats(prev => prev.map(c => c.id === chatId ? { ...c, unread_count: 0 } : c));
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) { toast.error("Failed to load messages"); }
    finally { setLoadingMessages(false); }
  }, []);

  // Iter 156.3 — useDepartmentActions invoked here (NOT earlier in the file)
  // because its `loadMessages` arg lives in Temporal Dead Zone above this
  // line. See test iteration_54.json RCA. Hook order matters in JS even
  // when ESLint can't see it.
  const {
    handleSetDepartment, handleReroute, handleAiReplyOnce, handleAiDeactivate,
  } = useDepartmentActions({
    selectedLead, setSelectedLead, setChats, loadMessages,
  });

  const handleSelectChat = (chat) => {
    setSelectedChat(chat); setShowQuickReplies(false); setReplyText("");
    setDetailsTab("info"); setShowFollowUp(false); setAttachment(null);
    setOrderData(null); setOrderLookup(""); setDetectedOrders([]);
    setProductData(null); setProductLookup(""); setDetectedProducts([]);
    loadMessages(chat.id);
    fetchNotes(chat.id);
    fetchIssueTags();
    detectOrders(chat.id);
    detectProducts(chat.id);
  };

  const handleSendMessage = async () => {
    if (!replyText.trim() || !selectedChat) return;
    setSending(true);
    try {
      let content = replyText.trim();
      // If replying to a specific message, prefix with quote
      if (replyToMsg) {
        const quotedSender = replyToMsg.direction === "incoming" ? "Customer" : "Agent";
        const quotedText = (replyToMsg.content || "").slice(0, 150);
        content = `> _${quotedSender}: ${quotedText}_\n\n${content}`;
      }
      await messagesApi.create({ lead_id: selectedChat.id, content, direction: "outgoing", send_whatsapp: true });
      setReplyText(""); setReplyToMsg(null); await loadMessages(selectedChat.id);
      toast.success("Sent via WhatsApp");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); inputRef.current?.focus(); }
  };

  const handleQuickReplySelect = (t) => {
    let content = t.content;
    // Resolve template variables
    if (selectedLead) {
      content = content.replace(/\{\{customer_name\}\}/gi, selectedLead.customer_name || selectedLead.name || "Customer");
      content = content.replace(/\{\{phone\}\}/gi, selectedLead.phone || "");
      content = content.replace(/\{\{category\}\}/gi, selectedLead.category || "");
      content = content.replace(/\{\{order_id\}\}/gi, selectedLead.last_order_number || "");
      content = content.replace(/\{\{agent_name\}\}/gi, user?.name || "");
    }
    setReplyText(content); setShowQuickReplies(false); inputRef.current?.focus();
  };
  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  // Insert a product URL into the composer (image-to-product POC).
  // Yash, Apr 28 2026 — agent picks one of the top-3 candidates after
  // visually matching the customer's photo, clicks "Insert link", and
  // the URL gets appended to the message they're typing. Focuses the
  // composer afterwards so they can hit send.
  const insertProductLinkIntoReply = (match) => {
    const link = match.url;
    if (!link) return;
    const existing = (replyText || "").trim();
    const next = existing ? `${existing}\n${link}` : link;
    setReplyText(next);
    inputRef.current?.focus();
    try { toast.success("Link added to your reply"); } catch (e) { /* sonner may not be loaded */ }
  };

  // Auto-resize textarea when content changes (e.g. AI fills text)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + "px";
    }
  }, [replyText]);

  // AI features
  // Iter 156.3 — 4 AI handlers extracted to hooks/useAiActions.js
  const {
    handleAiSuggest, handleAiExtract, handleAiSentiment, handleAiCategorize,
  } = useAiActions({
    selectedChat, setAiLoading, setReplyText, inputRef,
    setCustForm, setDetailsTab, setSelectedLead,
    loadMessages, fetchChats,
  });


  // Reassign
  // Iter 152.22m (May 5, 2026, Yash P0) — user reported "can't change
  // assigned agents" + "agent reverting to Sumit". Root cause had two
  // parts:
  //   1. handleReassign did NOT update selectedLead locally — ChatRightPanel's
  //      <select> reads lead.assigned_to from the selectedLead prop, so when
  //      fetchChats lagged, the dropdown would snap back to the old value
  //      mid-way through the user's next change.
  //   2. The old code called fetchChats(1, false, true) which resets paging +
  //      rewrites the entire chats array — that rewrite replaced
  //      selectedChat's assigned_to with whatever came back BEFORE our
  //      write had propagated (Mongo read-your-write isn't guaranteed
  //      across connections).
  // Fix: optimistically update selectedLead + chats FIRST, then fire the
  // API call, and only roll back on failure. We skip the heavy fetchChats
  // refresh entirely since the only thing that changes is `assigned_to`
  // (and its resolved name), which we already know.
  const handleReassign = async (agentId) => {
    if (!selectedLead) return;
    const prevAssignedTo = selectedLead.assigned_to;
    const prevAssignedName = selectedLead.assigned_to_name;
    const newId = agentId || null;
    const newAgentName = newId
      ? (agents.find(a => a.id === newId)?.name || agents.find(a => a.id === newId)?.email || null)
      : null;
    // Optimistic local update
    setSelectedLead(prev => prev ? { ...prev, assigned_to: newId, assigned_to_name: newAgentName } : prev);
    setChats(prev => prev.map(c => c.id === selectedLead.id
      ? { ...c, assigned_to: newId, assigned_to_name: newAgentName }
      : c));
    try {
      await leadActionsApi.reassign(selectedLead.id, newId);
      toast.success(newAgentName ? `Reassigned to ${newAgentName}` : "Chat unassigned");
      // Pull fresh messages so the "Chat transferred" system note appears
      await loadMessages(selectedLead.id);
    } catch (e) {
      // Roll back on failure
      setSelectedLead(prev => prev ? { ...prev, assigned_to: prevAssignedTo, assigned_to_name: prevAssignedName } : prev);
      setChats(prev => prev.map(c => c.id === selectedLead.id
        ? { ...c, assigned_to: prevAssignedTo, assigned_to_name: prevAssignedName }
        : c));
      toast.error(e?.response?.data?.detail || "Reassignment failed");
    }
  };

  // Iter 156.3 — 3 label handlers extracted to hooks/useLabelActions.js
  const {
    handleAddLabel, handleRemoveLabel, handleToggleLabel,
  } = useLabelActions({
    selectedLead, setSelectedLead, setChats,
    setNewLabelText, setShowLabelInput, fetchLabels,
  });

  const handleScheduleMessage = async () => {
    if (!selectedLead || !replyText.trim() || !scheduleDate || !scheduleTime) {
      toast.error("Please type a message and select date/time");
      return;
    }
    const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
    try {
      await scheduledMessagesApi.create({
        lead_id: selectedLead.id,
        message: replyText,
        scheduled_at: scheduledAt,
        type: scheduleType,
      });
      toast.success(`${scheduleType === "whatsapp" ? "Message" : "Reminder"} scheduled for ${scheduleDate} ${scheduleTime}`);
      setReplyText("");
      setShowScheduleModal(false);
      setScheduleDate("");
      setScheduleTime("");
    } catch (e) { toast.error("Failed to schedule"); }
  };


  // Customer details save
  const handleSaveCustDetails = async () => {
    if (!selectedLead) return; setSavingCust(true);
    try { await leadActionsApi.updateCustomerDetails(selectedLead.id, custForm); toast.success("Details saved"); await loadMessages(selectedLead.id); }
    catch (e) { toast.error("Failed to save"); } finally { setSavingCust(false); }
  };

  // Place admin order
  const handlePlaceAdminOrder = async () => {
    if (!selectedLead || !custForm.customer_name || !custForm.customer_phone || !custForm.customer_address || !custForm.customer_pincode) {
      toast.error("Fill Name, Phone, Address, Pincode"); return;
    }
    setSavingCust(true);
    try {
      const res = await leadActionsApi.placeAdminOrder(selectedLead.id, {
        lead_id: selectedLead.id,
        ...custForm,
        amount: parseFloat(custForm.amount) || 0,
        discount_percent: parseFloat(custForm.discount_percent) || 0,
        discount_amount: parseFloat(custForm.discount_amount) || 0,
        notes: custForm.notes || "",
        item_codes: custForm.item_codes || "",
      });
      toast.success(`Admin order placed: ${res.data.order_number}`);
      await loadMessages(selectedLead.id); fetchChats(1, false, true);
    } catch (e) { toast.error("Failed to place order"); } finally { setSavingCust(false); }
  };

  // Send attachment
  const handleSendAttachment = async () => {
    if (!selectedLead || !attachFile) return;
    setSendingAttach(true);
    try {
      const formData = new FormData();
      formData.append("file", attachFile);
      if (attachCaption) formData.append("caption", attachCaption);
      await leadActionsApi.sendAttachment(selectedLead.id, formData);
      toast.success("Attachment sent");
      setAttachment(null);
      await loadMessages(selectedLead.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send attachment"); }
    finally { setSendingAttach(false); }
  };

  // Drag & drop handlers for the message area
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    // Only hide overlay when leaving the drop zone entirely
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) setIsDragging(false);
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setAttachment(file);
  }, [setAttachment]);

  // Clipboard paste handler (for images)
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Give the pasted image a friendly name
          const ext = file.type.split("/")[1] || "png";
          const named = new File([file], `pasted-image.${ext}`, { type: file.type });
          setAttachment(named);
        }
        return;
      }
    }
  }, [setAttachment]);

  // Record existing order
  const handleRecordOrder = async () => {
    if (!selectedLead || !orderNum.trim()) { toast.error("Enter order number"); return; }
    try {
      await leadActionsApi.recordOrder(selectedLead.id, { lead_id: selectedLead.id, order_number: orderNum.trim(), amount: parseFloat(orderAmt) || 0 });
      toast.success("Order recorded"); setOrderNum(""); setOrderAmt(""); await loadMessages(selectedLead.id); fetchChats(1, false, true);
    } catch (e) { toast.error("Failed to record order"); }
  };

  // Payment link
  // Iter 156.3 — 5 payment-link handlers extracted to hooks/usePaymentLinkActions.js
  const {
    handleCreatePaymentLink, handleCopyLink, handleSendPaymentLink,
    handleSendQR, handleDownloadQR,
  } = usePaymentLinkActions({
    selectedLead, selectedChat,
    paymentAmount, paymentDesc, paymentCurrency, paymentProvider,
    payCustomerName, payCustomerPhone, payCustomerEmail,
    setCreatingPayment, setGeneratedLink, setQrBase64, setSendingQR,
    setReplyText, inputRef, setShowPaymentModal,
    setPaymentAmount, setPaymentDesc,
    generatedLink, qrBase64,
    loadMessages,
  });


  // Chat actions: close, open, mark read/unread
  const handleChatAction = async (action) => {
    if (!selectedChat) return;
    try {
      await leadActionsApi.chatAction(selectedChat.id, action);
      const labels = { close: "Chat closed", open: "Chat reopened", "mark-read": "Marked as read", "mark-unread": "Marked as unread" };
      toast.success(labels[action] || "Done");
      // Iter 152.22 — mirror WATI-status only. Legacy `status` field is no
      // longer written anywhere (user directive: "we will just go by WATI
      // statuses only and not rely on CRMs status of chats").
      const actionStatusMap = { close: "SOLVED", open: "OPEN" };
      const newWatiStatusFromAction = actionStatusMap[action];
      setChats(prev => {
        const updated = prev.map(c => {
          if (c.id !== selectedChat.id) return c;
          if (action === "close") return { ...c, wati_status: "SOLVED" };
          if (action === "open") return { ...c, wati_status: "OPEN" };
          if (action === "mark-read") return { ...c, unread_count: 0 };
          if (action === "mark-unread") return { ...c, unread_count: Math.max(c.unread_count || 0, 1) };
          return c;
        });
        if (newWatiStatusFromAction && statusFilter && !_statusMatchesFilter(newWatiStatusFromAction, statusFilter)) {
          return updated.filter(c => c.id !== selectedChat.id);
        }
        return updated;
      });
      if (action === "close" || action === "open") {
        setSelectedLead(prev => prev ? { ...prev, wati_status: action === "close" ? "SOLVED" : "OPEN" } : prev);
      }
      setShowChatActions(false);
    } catch (e) { toast.error("Failed to perform action"); }
  };

  // Send payment QR
  const handleSendPaymentQR = async () => {
    if (!selectedLead) return;
    setSendingPaymentQR(true);
    try {
      const res = await leadActionsApi.sendPaymentQR(selectedLead.id);
      if (res.data.success) {
        toast.success("Payment QR sent via WhatsApp!");
        await loadMessages(selectedChat.id);
      } else {
        toast.error(res.data.message || "Failed to send QR");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send QR"); }
    finally { setSendingPaymentQR(false); }
  };

  // Update WATI chat status
  const handleWatiStatus = async (newStatus) => {
    if (!selectedLead) return;
    try {
      const res = await leadActionsApi.watiStatus(selectedLead.id, newStatus);
      if (res.data.wati_warning) {
        toast.info(`CRM status updated. WATI: ${res.data.wati_warning}`);
      } else {
        toast.success(`Status updated to ${newStatus}`);
      }
      // Iter 152.22 — mirror WATI-status only (no more legacy `status` writes).
      setChats(prev => {
        const updated = prev.map(c => c.id === selectedLead.id ? { ...c, wati_status: newStatus } : c);
        if (statusFilter && !_statusMatchesFilter(newStatus, statusFilter)) {
          return updated.filter(c => c.id !== selectedLead.id);
        }
        return updated;
      });
      setSelectedLead(prev => prev ? { ...prev, wati_status: newStatus } : prev);
      setShowChatActions(false);
    } catch (e) { toast.error("Failed to update status"); }
  };

  // Assign WATI operator
  const handleWatiAssign = async () => {
    if (!selectedLead || !watiOperatorEmail.trim()) return;
    try {
      const res = await leadActionsApi.watiAssign(selectedLead.id, watiOperatorEmail.trim());
      if (res.data.success) {
        toast.success(`Assigned to ${watiOperatorEmail} on WATI`);
        setWatiOperatorEmail("");
      } else {
        toast.error(res.data.message || "Failed to assign");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign on WATI"); }
  };

  // Image product search
  const handleImageSearch = async (imageUrl) => {
    setSearchingImage(true);
    setImageSearchResults(null);
    try {
      const res = await aiApi.imageSearch({ image_url: imageUrl });
      setImageSearchResults(res.data);
      if (res.data.matches?.length > 0) {
        toast.success(`Found ${res.data.matches.length} matching products`);
      } else {
        toast.info("No matching products found on the website");
      }
    } catch (e) { toast.error("Image search failed"); }
    finally { setSearchingImage(false); }
  };

  // Follow-up
  const handleCreateFollowUp = async () => {
    if (!followUpDate || !followUpMessage.trim() || !selectedLead) return;
    setSavingFollowUp(true);
    try {
      await remindersApi.create({ lead_id: selectedLead.id, reminder_date: new Date(followUpDate).toISOString(), message: followUpMessage.trim() });
      toast.success("Follow-up set"); setShowFollowUp(false); setFollowUpDate(""); setFollowUpMessage(""); await loadMessages(selectedChat.id);
    } catch (e) { toast.error("Failed"); } finally { setSavingFollowUp(false); }
  };
  const handleCompleteReminder = async (id) => { try { await remindersApi.complete(id); toast.success("Done"); await loadMessages(selectedChat.id); } catch(e){} };
  const handleDeleteReminder = async (id) => { try { await remindersApi.delete(id); toast.success("Deleted"); await loadMessages(selectedChat.id); } catch(e){} };

  // Manual refresh for active chat
  const handleRefreshChat = async () => {
    if (!selectedChat) return;
    try {
      const res = await leadsApi.refreshMessages(selectedChat.id);
      setMessages(res.data.messages);
      if (res.data.new_messages > 0) {
        toast.success(`${res.data.new_messages} new message(s)`);
        setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        toast.info("No new messages");
      }
    } catch (e) { toast.error("Refresh failed"); }
  };

  const clearFilters = () => { setFilterCategory([]); setFilterAgent([]); setFilterLabel([]); setFilterDepartment([]); setShowFilters(false); };
  const hasActiveFilters = filterCategory.length > 0 || filterAgent.length > 0 || filterLabel.length > 0 || filterDepartment.length > 0;

  const syncLabel = (() => {
    if (!syncProgress) return "Syncing...";
    if (syncProgress.type === "contacts") return `Syncing: page ${syncProgress.progress || 0}/${syncProgress.total || "?"} | ${syncProgress.synced || 0} new`;
    return `Syncing: ${syncProgress.progress || 0}/${syncProgress.total || "?"} leads | ${syncProgress.messages_synced || 0} msgs`;
  })();
  const syncPct = syncProgress?.total > 0 ? Math.round((syncProgress.progress / syncProgress.total) * 100) : 0;

  // `formatTime`, `formatFullTime`, `getResponseTimer` are now imported
  // from `components/chat/utils`. They used to be defined inline here.

  const sentimentIcon = (s) => {
    if (s === "negative") return <AlertTriangle size={12} className="text-red-500" />;
    if (s === "urgent") return <AlertTriangle size={12} className="text-orange-500" />;
    return null;
  };

  return (
    // Iter 152.22l (May 5, 2026, Yash) — PWA viewport fix.
    // Removed `h-screen` (= `height: 100vh`). On iOS PWA + Android Chrome,
    // 100vh includes the URL/status-bar height even when hidden, so the
    // composer rendered BELOW the visible viewport. Without an explicit
    // height, `position: fixed` + `top: 0` + `bottom: 0` (or the
    // `!important bottom: calc(58px + safe-area-inset-bottom)` that
    // index.css adds on mobile) cleanly pins the page to the actual
    // visible area in both browser and standalone PWA modes.
    <div
      className="flex flex-col overflow-hidden fixed top-0 left-0 right-0 bottom-0 chats-page-shell"
      data-testid="chats-page"
    >
      {syncing && syncProgress && (
        <div className="px-4 py-2 bg-[--accent-pale] border-b border-[--border] flex items-center gap-3" data-testid="sync-progress">
          <Loader2 size={14} className="animate-spin text-[--accent]" />
          <span className="text-xs text-[--text-secondary]">{syncLabel}</span>
          <div className="flex-1 bg-[--border] rounded-full h-1.5 max-w-[200px]">
            <div className="bg-[--accent] h-1.5 rounded-full transition-all" style={{ width: `${syncPct}%` }} />
          </div>
        </div>
      )}
      {autoReplyActive && (
        <div className="px-4 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2" data-testid="auto-reply-indicator">
          <Sparkles size={13} className="text-amber-600" />
          <span className="text-[11px] font-bold text-amber-700">AI Auto-Reply is active</span>
          <span className="text-[10px] text-amber-500">- Incoming messages will receive automated responses</span>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {/* Left: Chat List — extracted to ChatListSidebar.jsx (Feb 2026 refactor) */}
        <ChatListSidebar
          chats={chats}
          sortedChats={sortedChats}
          agents={agents}
          labelOptions={labelOptions}
          total={total}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          selectedChat={selectedChat}
          user={user}
          syncing={syncing}
          sortBy={sortBy}
          setSortBy={setSortBy}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          hasActiveFilters={hasActiveFilters}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          filterAgent={filterAgent}
          setFilterAgent={setFilterAgent}
          filterLabel={filterLabel}
          setFilterLabel={setFilterLabel}
          filterDepartment={filterDepartment}
          setFilterDepartment={setFilterDepartment}
          clearFilters={clearFilters}
          search={search}
          setSearch={setSearch}
          countryFilter={countryFilter}
          setCountryFilter={setCountryFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          bulkMode={bulkMode}
          setBulkMode={setBulkMode}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          setShowBulkMenu={setShowBulkMenu}
          selectAllVisible={selectAllVisible}
          executeBulkAction={executeBulkAction}
          toggleBulkSelect={toggleBulkSelect}
          setShowNewChatModal={setShowNewChatModal}
          setShowImportModal={setShowImportModal}
          handleSyncAll={handleSyncAll}
          handleChatListScroll={handleChatListScroll}
          handleSelectChat={handleSelectChat}
          chatListRef={chatListRef}
          formatTime={formatTime}
        />

        {/* Center: Messages */}
        <div className={`${selectedChat ? "flex" : "hidden md:flex"} flex-col flex-1 chat-bg-pattern min-w-0`} data-testid="chat-messages-panel">
          {!selectedChat ? (
            <div className="flex items-center justify-center h-full text-[--text-muted]">
              <div className="text-center animate-fade-in">
                <MessageCircle size={48} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm font-bold text-[--text-secondary]" style={{ fontFamily: "Outfit, sans-serif" }}>Select a conversation</p>
                <p className="text-xs mt-1">Choose a chat to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              {/* Chat Header — extracted to components/chat/ChatHeader.jsx (iter 138, Feb 2026) */}
              <ChatHeader
                user={user}
                selectedChat={selectedChat}
                selectedLead={selectedLead}
                agents={agents}
                messages={messages}
                showMobileHeaderMenu={showMobileHeaderMenu}
                showDetails={showDetails}
                setSelectedChat={setSelectedChat}
                setShowMobileHeaderMenu={setShowMobileHeaderMenu}
                setShowDetails={setShowDetails}
                setShowLabelPicker={setShowLabelPicker}
                setShowCreateTicket={setShowCreateTicket}
                setShowPaymentModal={setShowPaymentModal}
                setShowNotifyTeamModal={setShowNotifyTeamModal}
                setShow360Phone={setShow360Phone}
                setQrBase64={setQrBase64}
                setGeneratedLink={setGeneratedLink}
                setPayCustomerName={setPayCustomerName}
                setPayCustomerEmail={setPayCustomerEmail}
                setPayCustomerPhone={setPayCustomerPhone}
                setPaymentAmount={setPaymentAmount}
                setPaymentCurrency={setPaymentCurrency}
                setPaymentDesc={setPaymentDesc}
                setTicketSubject={setTicketSubject}
                setTicketDescription={setTicketDescription}
                setTicketInternal={setTicketInternal}
                setTicketEmailOverride={setTicketEmailOverride}
                handleRefreshChat={handleRefreshChat}
                handleAiReplyOnce={handleAiReplyOnce}
                handleAiDeactivate={handleAiDeactivate}
                handleChatAction={handleChatAction}
                setShowExportModal={setShowExportModal}
                setShowShareModal={setShowShareModal}
                sentimentIcon={sentimentIcon}
              />

              <div className="flex flex-1 min-h-0">
                {/* Messages */}
                <div className="flex-1 flex flex-col min-w-0 relative" ref={dropZoneRef}
                  onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                  {/* Drag overlay */}
                  {isDragging && (
                    <div className="absolute inset-0 z-30 bg-[--accent]/10 border-2 border-dashed border-[--accent] rounded-xl flex items-center justify-center pointer-events-none" data-testid="drag-overlay">
                      <div className="bg-white/90 backdrop-blur-sm rounded-2xl px-8 py-6 shadow-xl text-center">
                        <Paperclip size={32} className="mx-auto mb-2 text-[--accent]" />
                        <p className="text-sm font-semibold text-[--text-primary]">Drop file here</p>
                        <p className="text-xs text-[--text-muted]">Image, video, or document</p>
                      </div>
                    </div>
                  )}
                  <MessageThread
                    loading={loadingMessages}
                    messages={messages}
                    msgEndRef={msgEndRef}
                    onImageClick={openImageGallery}
                    onImageSearch={handleImageSearch}
                    onReply={setReplyToMsg}
                    onResend={handleResendMessage}
                    onVerifyPayment={handleVerifyPayment}
                  />
                  {/* Image Search Results — extracted to components/chat/ImageSearchResultsPanel.jsx (iter 156.2, Feb 2026) */}
                  <ImageSearchResultsPanel
                    searching={searchingImage}
                    results={imageSearchResults}
                    onClose={() => setImageSearchResults(null)}
                    onInsertProduct={insertProductLinkIntoReply}
                  />
                  {showQuickReplies && (
                    <div className="border-t border-[--border] bg-white px-4 py-2 max-h-[140px] overflow-y-auto animate-scale-in" data-testid="quick-replies-picker">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-semibold text-[--text-muted] uppercase tracking-wider">Quick Replies</span>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setShowTemplateManager(true)} className="text-[10px] text-[--accent] hover:underline flex items-center gap-1" data-testid="manage-templates-btn"><Pencil size={10} /> Manage</button>
                          <button onClick={() => setShowQuickReplies(false)}><X size={12} className="text-[--text-muted]" /></button>
                        </div>
                      </div>
                      {quickReplies.length === 0 ? <p className="text-[11px] text-[--text-muted] py-2">No templates yet</p> : (
                        <div className="flex flex-wrap gap-1.5">{quickReplies.map(t => (
                          <button key={t.id} onClick={() => handleQuickReplySelect(t)} className="text-left px-3 py-1.5 bg-[--bg-cream] hover:bg-[--accent-pale] rounded-full transition-colors text-[11px] text-[--text-secondary] hover:text-[--accent] font-medium" data-testid={`quick-reply-${t.id}`}>{t.title}</button>
                        ))}</div>
                      )}
                    </div>
                  )}
                  {/* Reply Composer — extracted to components/chat/ReplyComposer.jsx (iter 140, Feb 2026) */}
                  <ReplyComposer
                    inputRef={inputRef}
                    attachInputRef={attachInputRef}
                    replyText={replyText}
                    setReplyText={setReplyText}
                    replyToMsg={replyToMsg}
                    setReplyToMsg={setReplyToMsg}
                    handleSendMessage={handleSendMessage}
                    sending={sending}
                    handleKeyDown={handleKeyDown}
                    handlePaste={handlePaste}
                    attachFile={attachFile}
                    setAttachFile={setAttachFile}
                    setAttachment={setAttachment}
                    attachPreviewUrl={attachPreviewUrl}
                    setAttachPreviewUrl={setAttachPreviewUrl}
                    attachCaption={attachCaption}
                    setAttachCaption={setAttachCaption}
                    sendingAttach={sendingAttach}
                    handleSendAttachment={handleSendAttachment}
                    showScheduleModal={showScheduleModal}
                    setShowScheduleModal={setShowScheduleModal}
                    scheduleType={scheduleType}
                    setScheduleType={setScheduleType}
                    scheduleDate={scheduleDate}
                    setScheduleDate={setScheduleDate}
                    scheduleTime={scheduleTime}
                    setScheduleTime={setScheduleTime}
                    handleScheduleMessage={handleScheduleMessage}
                    showQuickReplies={showQuickReplies}
                    setShowQuickReplies={setShowQuickReplies}
                    aiLoading={aiLoading}
                    handleAiSuggest={handleAiSuggest}
                    sendingPaymentQR={sendingPaymentQR}
                    handleSendPaymentQR={handleSendPaymentQR}
                  />
                </div>

                {/* Details Panel */}
                {showDetails && selectedLead && (
                  <div className="w-[300px] border-l border-[--border] bg-white overflow-y-auto flex-shrink-0 hidden lg:block" data-testid="details-panel">
                    {/* Tab bar — AI tab removed; Notes folded into Info */}
                    <div className="flex border-b border-[--border]">
                      {[{ k: "info", label: "Info" }, { k: "order", label: "Order" }, { k: "product", label: "Product" }, { k: "email", label: "Email" }].map(t => (
                        <button key={t.k} onClick={() => setDetailsTab(t.k)}
                          className={`flex-1 py-2 text-[11px] font-semibold transition-colors ${detailsTab === t.k ? "text-[--accent] border-b-2 border-[--accent]" : "text-[--text-muted] hover:text-[--text-secondary]"}`}
                          data-testid={`tab-${t.k}`}>{t.label}</button>
                      ))}
                    </div>
                    <div className="p-3 space-y-4">
                      {detailsTab === "info" && <>
                        <InfoTab lead={selectedLead} agents={agents} onReassign={handleReassign} user={user}
                          onWatiStatus={handleWatiStatus}
                          onSetDepartment={handleSetDepartment}
                          allLabels={allLabels} onAddLabel={handleAddLabel} onRemoveLabel={handleRemoveLabel} newLabelText={newLabelText} setNewLabelText={setNewLabelText} showLabelInput={showLabelInput} setShowLabelInput={setShowLabelInput} />
                        {/* Old Department selector block removed — now lives
                            inside InfoTab under "Assigned To (CRM)". */}
                        {/* AI Assistant + Payment Link + Issue Tags — extracted to
                            components/chat/AgentToolsPanel.jsx (iter 152.4, Feb 2026) */}
                        <AgentToolsPanel
                          lead={selectedLead}
                          onAiStart={handleAiReplyOnce}
                          onAiStop={handleAiDeactivate}
                          onPaymentLinkOpen={() => {
                            setShowPaymentModal(true);
                            setGeneratedLink(null);
                            setPaymentAmount("");
                            setPaymentDesc("");
                            setPaymentCurrency("INR");
                            setPayCustomerName(selectedLead?.customer_name || selectedLead?.name || "");
                            setPayCustomerPhone(selectedLead?.customer_phone || selectedLead?.phone || "");
                            setPayCustomerEmail(selectedLead?.customer_email || "");
                            setQrBase64(null);
                          }}
                          availableIssueTags={availableIssueTags}
                          onToggleIssueTag={toggleIssueTag}
                        />

                        {/* Notes — extracted to components/chat/NotesSection.jsx (iter 152.4, Feb 2026) */}
                        <NotesSection
                          notes={notes}
                          newNote={newNote}
                          setNewNote={setNewNote}
                          onAdd={addNote}
                          onDelete={deleteNote}
                          currentUserId={user?.id}
                        />

                      </>}
                      {detailsTab === "order" && (
                        <div className="space-y-0" data-testid="order-tab">
                          {/* Order Tracking / Lookup — extracted to components/chat/OrderTab.jsx (iter 152.6, Feb 2026) */}
                          <OrderTab
                            orderLookup={orderLookup}
                            setOrderLookup={setOrderLookup}
                            orderLoading={orderLoading}
                            orderData={orderData}
                            setOrderData={setOrderData}
                            detectedOrders={detectedOrders}
                            onLookup={lookupOrder}
                          />
                          {/* Website Orders panel — Yash, Apr 28 2026.
                              Live fetches from exoticindia.com using lead's
                              email/phone, plus a "Place Admin Order" form
                              the agent can submit one-click. */}
                          <WebsiteOrdersPanel leadId={selectedLead?.id} lead={selectedLead} />
                        </div>
                      )}
                      {detailsTab === "product" && (
                        <ProductLookupTab
                          priceScope={priceScope}
                          detectedProducts={detectedProducts}
                          productLookup={productLookup}
                          setProductLookup={setProductLookup}
                          productLoading={productLoading}
                          productData={productData}
                          setProductData={setProductData}
                          productSuggestions={productSuggestions}
                          setProductSuggestions={setProductSuggestions}
                          lookupProduct={lookupProduct}
                          selectedLead={selectedLead}
                          buildProductDetailsMessage={buildProductDetailsMessage}
                          copyProductSummary={copyProductSummary}
                          sendProductSummary={sendProductSummary}
                        />
                      )}
                      {detailsTab === "email" && <ZohoEmailTab lead={selectedLead} />}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {showTemplateManager && <TemplateManager templates={quickReplies} onClose={() => { setShowTemplateManager(false); fetchQuickReplies(); }} currentUser={user} />}
      {/* Payment Link Modal — extracted to components/chat/PaymentLinkModal.jsx (iter 156.2, Feb 2026) */}
      <PaymentLinkModal
        open={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setGeneratedLink(null); setQrBase64(null); }}
        paymentProvider={paymentProvider}
        setPaymentProvider={setPaymentProvider}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        paymentCurrency={paymentCurrency}
        setPaymentCurrency={setPaymentCurrency}
        paymentDesc={paymentDesc}
        setPaymentDesc={setPaymentDesc}
        payCustomerName={payCustomerName}
        setPayCustomerName={setPayCustomerName}
        payCustomerPhone={payCustomerPhone}
        setPayCustomerPhone={setPayCustomerPhone}
        payCustomerEmail={payCustomerEmail}
        setPayCustomerEmail={setPayCustomerEmail}
        providersConfig={providersConfig}
        creatingPayment={creatingPayment}
        generatedLink={generatedLink}
        setGeneratedLink={setGeneratedLink}
        qrBase64={qrBase64}
        setQrBase64={setQrBase64}
        sendingQR={sendingQR}
        onCreate={handleCreatePaymentLink}
        onDownloadQR={handleDownloadQR}
        onSendQR={handleSendQR}
        onCopyLink={handleCopyLink}
        onSendLinkInChat={handleSendPaymentLink}
      />
      {/* Iter 156.2 — orphan EscalateChatModal removed (no trigger). The
          escalation flow now uses CreateZohoTicketModal via `escalate-to-email-btn`
          in the chat header. The component file remains at
          components/chat/EscalateChatModal.jsx for future re-wiring. */}
      {/* Notify Team modal — manual chat-to-Sales/CS email */}
      {showExportModal && (
        <ExportChatModal lead={selectedLead} onClose={() => setShowExportModal(false)} />
      )}
      {showShareModal && (
        <ShareChatModal lead={selectedLead} onClose={() => setShowShareModal(false)} />
      )}
      {/* Notify Team modal — extracted to components/chat/NotifyTeamModal.jsx (iter 156.2, Feb 2026) */}
      <NotifyTeamModal
        open={showNotifyTeamModal}
        onClose={() => setShowNotifyTeamModal(false)}
        notifyTeamConfig={notifyTeamConfig}
        notifyRecipient={notifyRecipient}
        setNotifyRecipient={setNotifyRecipient}
        notifyReason={notifyReason}
        setNotifyReason={setNotifyReason}
        notifyNote={notifyNote}
        setNotifyNote={setNotifyNote}
        notifySending={notifySending}
        onSend={handleSendNotifyTeam}
      />
      {/* Create Zoho Ticket Modal (from WhatsApp chat) */}
      {/* Create Zoho Ticket Modal — extracted to components/chat/CreateZohoTicketModal.jsx (iter 156.2, Feb 2026) */}
      <CreateZohoTicketModal
        open={showCreateTicket}
        onClose={() => setShowCreateTicket(false)}
        selectedLead={selectedLead}
        ticketInternal={ticketInternal}
        setTicketInternal={setTicketInternal}
        ticketEmailOverride={ticketEmailOverride}
        setTicketEmailOverride={setTicketEmailOverride}
        ticketSubject={ticketSubject}
        setTicketSubject={setTicketSubject}
        ticketDescription={ticketDescription}
        setTicketDescription={setTicketDescription}
        creatingTicket={creatingTicket}
        onCreate={handleCreateZohoTicket}
      />
      {/* Mobile FAB: "New Chat" — only when the list panel is visible (no chat selected)
          and the user has permission (admin / super_admin). Hidden on tablet+ via CSS. */}
      {!selectedChat && (user?.role === "admin" || user?.role === "super_admin") && (
        <button
          onClick={() => setShowImportModal(true)}
          className="fab-chat hidden max-sm:flex"
          data-testid="mobile-new-chat-fab"
          aria-label="Import new contact"
          title="Import new contact"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}
      {/* New Chat (WATI template) modal */}
      <NewChatModal
        open={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        onStarted={() => { setShowNewChatModal(false); fetchChats(1); }}
      />
      {/* Customer 360 drawer (overlays everything) */}
      {show360Phone && (
        <Customer360Panel phone={show360Phone} onClose={() => setShow360Phone(null)} mode="drawer" />
      )}
      {/* Import Contact Modal */}
      {/* Import Contact Modal — extracted to components/chat/ImportContactModal.jsx (iter 156.2, Feb 2026) */}
      <ImportContactModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importPhone={importPhone}
        setImportPhone={setImportPhone}
        importing={importing}
        onImport={handleImportContact}
      />
      {previewGallery && (
        <ImagePreviewModal
          images={previewGallery.images}
          initialIndex={previewGallery.index}
          onClose={() => setPreviewGallery(null)}
        />
      )}
      {/* Legacy single-image preview — kept so any external callers don't break */}
      {previewImage && !previewGallery && (
        <ImagePreviewModal
          images={[previewImage]}
          initialIndex={0}
          onClose={() => setPreviewImage(null)}
        />
      )}
      {/* AI Payment Verification verdict modal — extracted to components/chat/PaymentVerificationModal.jsx (iter 156.2, Feb 2026) */}
      <PaymentVerificationModal
        open={!!verifyingPaymentMsgId}
        onClose={() => { setVerifyingPaymentMsgId(null); setPaymentVerdict(null); }}
        paymentVerdict={paymentVerdict}
      />
    </div>
  );
};

/* ---- ChatItem, avatar gradient, and response timer helpers have moved
 * to components/chat/ChatListSidebar.jsx and components/chat/utils.js
 * as part of the Feb 2026 ChatsPage refactor.
 * --------------------------------------------------------------- */
/* ----------------------------------------------------------------
 * Iter 134 (Apr 30, 2026) — the bottom presentation components
 * (CategoryBadge, InfoTab, OrderTab, AiTab, CustInput, AiBtn,
 * Sec, Row) have moved to `components/chat/ChatRightPanel.jsx`.
 * They are imported at the top of this file.
 * --------------------------------------------------------------- */





export default ChatsPage;
